import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHead from "@/components/SectionHead";
import FinalCTA from "@/components/FinalCTA";
import { APPLYKAR_URL, APPLYKAR_CANDIDATES } from "@/lib/constants";
import ScrollRail from "@/components/ScrollRail";
import { PipelineRail } from "@/components/Rails";
import LottiePlayer from "@/components/LottiePlayer";
import networkAnim from "@/assets/lottie/network-nodes.json";

const SEEKERS = [
  ["One profile, every opportunity", "Build a single profile and use it for private jobs, freelance projects and skill-selling."],
  ["Daily government job updates", "Sarkari job alerts delivered daily, alongside private openings, so nothing is missed."],
  ["AI Apply and Auto Apply", "AI Apply tailors each application. Auto Apply submits to matching jobs for you."],
  ["Sell your skills", "Freelancers and specialists can list skills and win projects, not just jobs."],
];
const RECRUITERS = [
  ["Free job posting", "Employers and recruiters post jobs at no cost and reach a ready candidate base."],
  ["Recruitment consultancy mode", "Run a whole agency: manage team members, clients, invoices and more from one workspace."],
  ["A website, ready to go", "Small agencies get a professional web presence without a separate build."],
  ["A dedicated portal to promote jobs", "Post on a portal built for Indian hiring and get your roles in front of active candidates."],
];
const STEPS = [["Create one profile", "Skills, experience, preferences."], ["Discover", "Private, government and freelance listings in one feed."], ["Apply", "Manually, with AI Apply, or hands-free with Auto Apply."], ["Get hired", "Employers and agencies manage the pipeline end to end."]];

const Grid = ({ items }) => (
  <dl className="grid sm:grid-cols-2 gap-5">
    {items.map(([t, d]) => (
      <div key={t} data-arm="ak" className="hp-card p-6">
        <div className="hp-arm-bar h-1 w-10 rounded" />
        <dt className="hp-h3 mt-4">{t}</dt><dd className="hp-body mt-2 text-[15px]">{d}</dd>
      </div>
    ))}
  </dl>
);

export default function Hiring() {
  return (
    <>
      <Seo title="Hiring & Talent" description="One portal for Indian job seekers, recruiters and recruitment agencies: private jobs, daily government updates, freelance work, AI Apply and free job posting." path="/hiring" />
      <section data-arm="ak" className="hp-hero-light hp-immersive" style={{ minHeight: "65vh" }}>
        <img 
          src="/images/hiring_talent_1791101753921.jpg" 
          className="hp-immersive-img"
          style={{ objectPosition: 'center 40%' }}
          alt="Talent and hiring" 
        />
        <div
          className="absolute inset-0 pointer-events-none bg-gradient-to-b lg:bg-gradient-to-r from-white via-white/95 to-white/10"
          aria-hidden="true"
        />
        <div className="hop-container relative z-10 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center w-full">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="hp-label hp-arm-accent">Hiring &amp; Talent</p>
              <h1 className="hp-h1 mt-5">A super job portal built for India.</h1>
              <p className="hp-lead mt-7" style={{ color: "#3c4768" }}>We own and operate a hiring platform where candidates get more from a single profile, recruiters post for free, and recruitment consultancies run their whole business. Hiring works better when both sides are well served.</p>
              {APPLYKAR_CANDIDATES && <p className="mt-6 font-display text-4xl text-hop-blue">{APPLYKAR_CANDIDATES} <span className="text-base font-sans text-slate-600">candidates on the portal</span></p>}
            </Reveal>
          </div>
          <div className="hidden lg:block lg:col-span-4">
            <LottiePlayer className="hp-lottie-hero drop-shadow-xl opacity-90 mix-blend-multiply" data={networkAnim} label="Candidates, recruiters and agencies connected through one portal" />
          </div>
        </div>
      </section>
      <section className="hp-section hp-tint"><div className="hop-container">
        <SectionHead arm="ak" label="For candidates" title="Your candidates will love this." lead="Everything a job seeker needs, from one login." />
        <div className="mt-10"><Grid items={SEEKERS} /></div>
      </div></section>
      <section className="hp-section"><div className="hop-container">
        <SectionHead arm="ak" label="For recruiters and agencies" title="Grow big, even as a small agency." lead="Free posting for every recruiter, plus a full consultancy mode for firms that manage teams, clients and invoices." />
        <div className="mt-10"><Grid items={RECRUITERS} /></div>
      </div></section>
      <section className="hp-section hp-tint relative overflow-hidden bg-[#f7f7f5]">
        {/* Background Image with strong readable gradient fade */}
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }} aria-hidden="true">
          <img
            src="/images/hiring_talent_1791101753921.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#f7f7f5] via-[#f7f7f5]/90 to-[#f7f7f5]/10" />
        </div>
        <div className="hop-container relative" style={{ zIndex: 10 }}>
          <SectionHead arm="ak" label="How it works" title="From profile to offer in four steps." />
          <div className="mt-12" data-arm="ak">
            <ScrollRail inline={false} rail={(p) => <PipelineRail p={p} steps={STEPS} />}>
              <ol>
                {STEPS.map(([t, d], i) => (
                  <li key={t} className="hp-beat bg-white/40 p-6 rounded-lg backdrop-blur-md border border-slate-100/60 shadow-sm relative mb-4">
                    <span className="font-display text-4xl text-[var(--arm-ak)] hp-stat-num">{i + 1}</span>
                    <h3 className="hp-h3 mt-2">{t}</h3>
                    <p className="hp-body text-[16px] mt-2 max-w-md">{d}</p>
                  </li>
                ))}
              </ol>
            </ScrollRail>
          </div>
        </div>
      </section>
      <section data-arm="ak" className="hp-section"><div className="hop-container text-center max-w-2xl">
        <h2 className="hp-h2">Ready to try it?</h2>
        <p className="hp-lead mt-4">The portal runs at its own address. Candidates and recruiters can sign up in minutes.</p>
        <a href={APPLYKAR_URL} target="_blank" rel="noopener noreferrer" className="hp-btn text-white mt-8 inline-flex" style={{ background: "var(--arm-ak)" }}>Go to Applykar <ArrowUpRight className="w-4 h-4" aria-hidden="true" /></a>
      </div></section>
      <FinalCTA />
    </>
  );
}
